import { Fragment } from 'react'
import { useTranslation } from 'next-i18next'

import DropdownMenu from '~/components/DropdownMenu/DropdownMenu'
import MultiSelect from '~/components/MultiSelect'
import useSettingsFormContext from '~/hooks/useSettingsFormContext'
import { FieldError } from '~/components/FieldError'
import { GradeLevel, LevelOfEducation, Subject, Subjects } from '~/types/booking'
import { UserRole } from '~/types/user-info-types'

export default function SubjectsSetting({ role }: { role: string }): React.ReactElement {
  const namespace = role === UserRole.TUTOR ? 'tutorPanel' : 'studentPanel'
  const { t } = useTranslation([namespace, 'booking'])
  const { fields, errors, setFields } = useSettingsFormContext()
  const { subjects } = fields
  const mathGradeLevelState = subjects.subjects?.find((elem) => elem.name === Subject.MATH)

  const dropdownMenuHeader = t(`${namespace}:settings.labelSubjects`)
  const selectedStateLabel = subjects.subjects?.map((elem) => t(`booking:subjects.${elem.name}`)).join(', ')

  const handleOnLevelOfEducationClick = (item: LevelOfEducation): void => {
    setFields((prevState) => {
      const prevItems = prevState.subjects.levelsOfEducation
      const isExistingItem = prevItems.includes(item)

      return {
        ...prevState,
        subjects: {
          ...prevState.subjects,
          levelsOfEducation: isExistingItem ? prevItems.filter((elem) => elem !== item) : [...prevItems, item],
        },
      }
    })
  }

  const handleOnClick = (subject: Subject): void => {
    setFields((prevState) => {
      const prevSubjects = prevState.subjects.subjects || []
      let newSubjects: Subjects['subjects'] = []

      if (subject === Subject.MATH) {
        newSubjects = [...prevSubjects, { name: subject, gradeLevels: [] }]
      } else {
        newSubjects = [...prevSubjects, { name: subject }]
      }

      return {
        ...prevState,
        subjects: {
          ...prevState.subjects,
          subjects: prevSubjects.some((elem) => elem.name === subject)
            ? prevSubjects.filter((elem) => elem.name !== subject)
            : newSubjects,
        },
      }
    })
  }

  const handleOnGradeLevelItemClick = (gradeLevel: GradeLevel): void => {
    const currState = mathGradeLevelState?.gradeLevels

    if (!currState) {
      return
    }

    setFields((prevState) => {
      let newGradeLevels: Subjects['subjects'][number]['gradeLevels']

      if (!currState.includes(gradeLevel)) {
        newGradeLevels = [...currState, gradeLevel]
      } else {
        newGradeLevels = [...currState?.filter((elem) => elem !== gradeLevel)]
      }

      return {
        ...prevState,
        subjects: {
          ...prevState.subjects,
          subjects: [
            ...subjects.subjects?.filter((elem) => elem.name !== Subject.MATH),
            { name: Subject.MATH, gradeLevels: newGradeLevels },
          ],
        },
      }
    })
  }

  function shouldShowGradeLevels(): boolean {
    return (
      (fields.schoolCountry === 'Norway' || role === 'mentor') &&
      Array.isArray(subjects.levelsOfEducation) &&
      subjects.levelsOfEducation.includes(LevelOfEducation.GRADE_11_TO_13) &&
      subjects.subjects?.some((elem) => elem.name === Subject.MATH)
    )
  }

  const levelOfEducationItems = Object.values(LevelOfEducation).map((elem) => ({
    value: elem,
    label: t(`booking:subjects.levelsOfEducation.${elem}`),
  }))

  const subjectItems = Object.values(Subject).map((elem) => ({
    value: elem,
    label: t(`booking:subjects.${elem}`),
  }))

  const gradeLevelItems = Object.values(GradeLevel).map((elem) => ({
    value: elem,
    label: t(`booking:gradeLevels.${elem}`),
  }))

  return (
    <div>
      <DropdownMenu header={dropdownMenuHeader} selectedStateLabel={selectedStateLabel}>
        <div className="space-y-2">
          <div className="space-y-0.5">
            <span className="text-xxs text-gray-light-fade">
              {t(`${namespace}:settings.descriptionLevelOfEducation`)}
            </span>
            <MultiSelect
              handleOnClick={handleOnLevelOfEducationClick}
              items={levelOfEducationItems}
              state={fields.subjects.levelsOfEducation}
            />
          </div>

          {subjects.levelsOfEducation && (
            <Fragment>
              <hr className="border-blue-iris/10 border-t" />
              <div className="space-y-0.5">
                <span className="text-xxs text-gray-light-fade">{t(`${namespace}:settings.descriptionSubjects`)}</span>
                <MultiSelect
                  handleOnClick={handleOnClick}
                  items={subjectItems}
                  state={subjects.subjects?.map((elem) => elem.name) || []}
                />
              </div>
            </Fragment>
          )}

          {mathGradeLevelState?.gradeLevels && shouldShowGradeLevels() && (
            <Fragment>
              <hr className="border-blue-iris/10 border-t" />
              <div className="space-y-0.5">
                <span className="text-xxs text-gray-light-fade">
                  {t(`${namespace}:settings.descriptionGradeLevels`)}
                </span>
                <MultiSelect
                  handleOnClick={handleOnGradeLevelItemClick}
                  items={gradeLevelItems}
                  state={mathGradeLevelState?.gradeLevels}
                />
              </div>
            </Fragment>
          )}
        </div>
      </DropdownMenu>
      <FieldError error={errors.levelsOfEducation} />
      <FieldError error={errors.subjects} />
    </div>
  )
}
